import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';

// ---------------------------------------------------------------------------
// Filter presets — taste-forward, fashion/editorial vocabulary. Implemented
// using only CSS / canvas filter strings so the saved blob is the exact
// rasterized result the user previews. No external deps, no WebGL.
// ---------------------------------------------------------------------------
const FILTER_PRESETS = [
  { id: 'original',     label: 'Original',      filter: () => 'none' },
  { id: 'clean',        label: 'Clean',         filter: (i) => `contrast(${1 + 0.05 * i}) saturate(${1 + 0.05 * i})` },
  { id: 'soft_light',   label: 'Soft Light',    filter: (i) => `brightness(${1 + 0.05 * i}) contrast(${1 - 0.05 * i}) saturate(${1 - 0.05 * i})` },
  { id: 'warm',         label: 'Warm',          filter: (i) => `sepia(${0.10 * i}) saturate(${1 + 0.15 * i}) hue-rotate(${-5 * i}deg)` },
  { id: 'cool',         label: 'Cool',          filter: (i) => `saturate(${1 + 0.05 * i}) hue-rotate(${10 * i}deg) brightness(${1 + 0.02 * i})` },
  { id: 'editorial',    label: 'Editorial',     filter: (i) => `contrast(${1 + 0.15 * i}) saturate(${1 - 0.10 * i}) brightness(${1 + 0.02 * i})` },
  { id: 'luxe',         label: 'Luxe',          filter: (i) => `contrast(${1 + 0.10 * i}) saturate(${1 + 0.10 * i}) sepia(${0.05 * i})` },
  { id: 'film',         label: 'Film',          filter: (i) => `sepia(${0.15 * i}) contrast(${1 + 0.05 * i}) brightness(${1 - 0.02 * i}) saturate(${1 - 0.10 * i})` },
  { id: 'high_contrast',label: 'High Contrast', filter: (i) => `contrast(${1 + 0.30 * i}) saturate(${1 + 0.10 * i})` },
  { id: 'bw',           label: 'Black & White', filter: (i) => `grayscale(${i}) contrast(${1 + 0.10 * i})` },
  { id: 'vintage',      label: 'Vintage',       filter: (i) => `sepia(${0.30 * i}) contrast(${1 - 0.05 * i}) brightness(${1 + 0.05 * i}) saturate(${1 - 0.10 * i})` },
  { id: 'product_shot', label: 'Product Shot',  filter: (i) => `contrast(${1 + 0.10 * i}) brightness(${1 + 0.05 * i}) saturate(${1 - 0.05 * i})` },
  { id: 'closet_clean', label: 'Closet Clean',  filter: (i) => `contrast(${1 + 0.05 * i}) brightness(${1 + 0.03 * i}) saturate(${1 - 0.05 * i})` },
];

const DEFAULT_ADJUSTMENTS = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
};

const adjustmentsToFilter = ({ brightness, contrast, saturation, warmth }) => {
  // Map -1..1 sliders to safe ranges.
  const b = 1 + brightness * 0.5;
  const c = 1 + contrast * 0.5;
  const s = 1 + saturation * 0.5;
  // Warmth: positive = warmer (sepia + slight hue), negative = cooler
  const sepia = warmth > 0 ? warmth * 0.20 : 0;
  const hue = warmth < 0 ? warmth * 18 : 0; // negative warmth → cool tint
  return `brightness(${b}) contrast(${c}) saturate(${s})${sepia > 0 ? ` sepia(${sepia})` : ''}${hue !== 0 ? ` hue-rotate(${hue}deg)` : ''}`;
};

const composeFilterString = (presetId, intensity, adjustments) => {
  const preset = FILTER_PRESETS.find(p => p.id === presetId) || FILTER_PRESETS[0];
  const presetStr = preset.filter(intensity);
  const adjustStr = adjustmentsToFilter(adjustments);
  if (presetStr === 'none') return adjustStr;
  return `${presetStr} ${adjustStr}`;
};

// Bake the cropped + rotated + filtered image into a JPEG blob.
// canvas.toBlob() strips EXIF/GPS automatically because we redraw to bytes.
const bakeBlob = (imageSrc, pixelCrop, rotation, filterString) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onerror = reject;
    image.onload = () => {
      try {
        const safeArea = Math.max(image.width, image.height) * 2;
        const c = document.createElement('canvas');
        c.width = safeArea;
        c.height = safeArea;
        const ctx = c.getContext('2d');
        // Filters apply to subsequent drawImage operations.
        ctx.filter = filterString || 'none';
        ctx.translate(safeArea / 2, safeArea / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-image.width / 2, -image.height / 2);
        ctx.drawImage(image, 0, 0);
        const data = ctx.getImageData(0, 0, safeArea, safeArea);
        c.width = pixelCrop.width;
        c.height = pixelCrop.height;
        ctx.putImageData(
          data,
          Math.round(0 - safeArea / 2 + image.width / 2 - pixelCrop.x),
          Math.round(0 - safeArea / 2 + image.height / 2 - pixelCrop.y),
        );
        c.toBlob((blob) => {
          if (!blob) return reject(new Error('Canvas empty'));
          resolve(blob);
        }, 'image/jpeg', 0.92);
      } catch (e) {
        reject(e);
      }
    };
    image.src = imageSrc;
  });

// Filter swatch thumbnail using a downscaled <canvas>.
const FilterSwatch = ({ src, preset, intensity, active, onClick }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !src) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = ref.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const w = 64, h = 64;
      canvas.width = w; canvas.height = h;
      ctx.filter = preset.filter(intensity);
      const scale = Math.max(w / img.width, h / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    };
    img.src = src;
  }, [src, preset, intensity]);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`photo-editor-filter-${preset.id}`}
      className={`flex flex-col items-center gap-1.5 flex-shrink-0 ${active ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`}
    >
      <canvas
        ref={ref}
        className={`w-16 h-16 rounded-xl bg-[#222] ${active ? 'ring-2 ring-[#C7A96B]' : 'ring-1 ring-white/10'}`}
      />
      <span className={`text-[9px] uppercase tracking-[0.12em] ${active ? 'text-[#C7A96B]' : 'text-white/60'}`}>
        {preset.label}
      </span>
    </button>
  );
};

const AdjustmentSlider = ({ label, value, onChange, testId, min = -1, max = 1, step = 0.05 }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/60">{label}</span>
      <span className="font-sans text-[10px] text-white/40">{Math.round(value * 100)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-[#C7A96B]"
      data-testid={testId}
    />
  </div>
);

export const PhotoEditor = ({ src, aspect = 4 / 5, onCancel, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [presetId, setPresetId] = useState('original');
  const [intensity, setIntensity] = useState(1);
  const [adjustments, setAdjustments] = useState(DEFAULT_ADJUSTMENTS);
  const [tab, setTab] = useState('filters'); // 'filters' | 'adjust' | 'transform'
  const [previewBefore, setPreviewBefore] = useState(false);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixelCrop(areaPixels);
  }, []);

  const liveFilter = useMemo(
    () => composeFilterString(presetId, intensity, adjustments),
    [presetId, intensity, adjustments],
  );

  const cropperMediaStyle = useMemo(
    () => ({ filter: previewBefore ? 'none' : liveFilter, transition: 'filter 150ms ease' }),
    [liveFilter, previewBefore],
  );

  const handleSave = async () => {
    if (!pixelCrop) return;
    setSaving(true);
    try {
      const blob = await bakeBlob(src, pixelCrop, rotation, liveFilter);
      onSave(blob);
    } catch (err) {
      console.error('PhotoEditor save failed', err);
      alert('Could not save edited photo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPresetId('original');
    setIntensity(1);
    setAdjustments(DEFAULT_ADJUSTMENTS);
    setRotation(0);
    setZoom(1);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0D0D0D] flex flex-col overflow-hidden"
      style={{
        height: '100dvh',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="photo-editor"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 text-white flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="font-sans text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white px-2 py-2 min-h-[44px]"
          data-testid="photo-editor-cancel"
        >
          Cancel
        </button>
        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/60">Edit Photo</span>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !pixelCrop}
          className="font-sans text-[11px] uppercase tracking-[0.2em] text-[#C7A96B] disabled:opacity-40 px-2 py-2 min-h-[44px]"
          data-testid="photo-editor-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Cropper canvas — takes all remaining vertical space, never overflows */}
      <div className="relative flex-1 min-h-0">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          objectFit="contain"
          style={{ mediaStyle: cropperMediaStyle }}
        />
      </div>

      {/* Bottom panel — tabs + controls. max-h prevents pushing the cropper
          off-screen on small phones; the inner area scrolls when needed. */}
      <div className="bg-[#0D0D0D] text-white flex-shrink-0 max-h-[55vh] overflow-y-auto">
        <div className="px-4 pt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-1 flex-shrink-0">
            {['filters', 'adjust', 'transform'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                data-testid={`photo-editor-tab-${t}`}
                className={`px-3 py-2 min-h-[40px] rounded-full font-sans text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  tab === t ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white/80'
                }`}
              >
                {t === 'filters' ? 'Filters' : t === 'adjust' ? 'Polish' : 'Transform'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Tap-toggle Before/After — always visible in portrait. */}
            <button
              type="button"
              onClick={() => setPreviewBefore(p => !p)}
              onMouseDown={() => setPreviewBefore(true)}
              onMouseUp={() => setPreviewBefore(false)}
              onMouseLeave={() => setPreviewBefore(false)}
              onTouchStart={() => setPreviewBefore(true)}
              onTouchEnd={() => setPreviewBefore(false)}
              data-testid="photo-editor-compare"
              className={`font-sans text-[10px] uppercase tracking-[0.18em] px-3 py-2 min-h-[40px] border rounded-full transition-colors ${
                previewBefore
                  ? 'border-[#C7A96B] text-[#C7A96B] bg-[#C7A96B]/10'
                  : 'border-white/15 text-white/70 hover:border-white/40'
              }`}
            >
              {previewBefore ? 'Before' : 'After'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              data-testid="photo-editor-reset"
              className="font-sans text-[10px] uppercase tracking-[0.18em] text-white/60 hover:text-white px-2 py-2 min-h-[40px]"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Filter strip */}
        {tab === 'filters' && (
          <div className="px-5 py-4 overflow-x-auto" data-testid="photo-editor-filters">
            <div className="flex items-end gap-3 pb-1">
              {FILTER_PRESETS.map((preset) => (
                <FilterSwatch
                  key={preset.id}
                  src={src}
                  preset={preset}
                  intensity={1}
                  active={presetId === preset.id}
                  onClick={() => setPresetId(preset.id)}
                />
              ))}
            </div>
            {presetId !== 'original' && (
              <div className="pt-3">
                <AdjustmentSlider
                  label="Intensity"
                  value={intensity}
                  onChange={setIntensity}
                  min={0}
                  max={1}
                  step={0.05}
                  testId="photo-editor-intensity"
                />
              </div>
            )}
          </div>
        )}

        {/* Polish / Adjust panel — manual control over lighting and tone only.
           Wording avoids body/beauty terms by design. */}
        {tab === 'adjust' && (
          <div className="px-5 py-4 space-y-4" data-testid="photo-editor-adjust">
            <AdjustmentSlider label="Brightness" value={adjustments.brightness}
              onChange={(v) => setAdjustments({ ...adjustments, brightness: v })}
              testId="photo-editor-adjust-brightness" />
            <AdjustmentSlider label="Contrast" value={adjustments.contrast}
              onChange={(v) => setAdjustments({ ...adjustments, contrast: v })}
              testId="photo-editor-adjust-contrast" />
            <AdjustmentSlider label="Saturation" value={adjustments.saturation}
              onChange={(v) => setAdjustments({ ...adjustments, saturation: v })}
              testId="photo-editor-adjust-saturation" />
            <AdjustmentSlider label="Warmth" value={adjustments.warmth}
              onChange={(v) => setAdjustments({ ...adjustments, warmth: v })}
              testId="photo-editor-adjust-warmth" />
            <p className="font-sans text-[10px] text-white/40 leading-relaxed pt-1">
              Polish adjusts lighting and tone only — no face, body, or beauty scoring.
            </p>
          </div>
        )}

        {/* Transform — zoom + rotate */}
        {tab === 'transform' && (
          <div className="px-5 py-4 space-y-5" data-testid="photo-editor-transform">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/60">Zoom</span>
                <span className="font-sans text-[10px] text-white/40">{zoom.toFixed(2)}x</span>
              </div>
              <input type="range" min={1} max={3} step={0.05} value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full accent-[#C7A96B]"
                data-testid="photo-editor-zoom" />
            </div>
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                data-testid="photo-editor-rotate-left"
                className="font-sans text-[11px] uppercase tracking-[0.2em] text-white/80 px-4 py-2 border border-white/20 rounded-full">
                ↺ Rotate
              </button>
              <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/40">{rotation}°</span>
              <button type="button" onClick={() => setRotation((r) => (r + 90) % 360)}
                data-testid="photo-editor-rotate-right"
                className="font-sans text-[11px] uppercase tracking-[0.2em] text-white/80 px-4 py-2 border border-white/20 rounded-full">
                Rotate ↻
              </button>
            </div>
          </div>
        )}

        <div className="h-[env(safe-area-inset-bottom,0px)]" />
      </div>
    </div>
  );
};

export default PhotoEditor;